import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmationModal from '../ui/ConfirmationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLE_LABELS = {
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual'
};

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};

const CurrentSubscription = ({ sessionToken, onUpdate }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Confirmation modal states
  const [reactivateModal, setReactivateModal] = useState({ isOpen: false, isProcessing: false });

  useEffect(() => {
    if (sessionToken) {
      fetchSubscription();
    }
  }, [sessionToken]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscription(response.data.subscription);
      if (onUpdate) onUpdate(response.data.subscription);
      
      // If no subscription found, try to verify any pending checkout
      if (!response.data.subscription) {
        await verifyPendingCheckout();
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyPendingCheckout = async () => {
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/subscriptions/verify-checkout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.subscription) {
        setSubscription(response.data.subscription);
        if (onUpdate) onUpdate(response.data.subscription);
        setMessage({
          type: 'success',
          text: `🎉 ${response.data.message} +${response.data.credits_added} credits added!`
        });
        
        // Reload the page after 2 seconds to refresh credit balance
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      // Silently fail - no pending subscription is normal
      if (err.response?.status !== 404) {
        console.error('Failed to verify checkout:', err);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleManualVerify = async () => {
    setMessage(null);
    await verifyPendingCheckout();
  };

  const handleReactivate = async () => {
    setReactivateModal(prev => ({ ...prev, isProcessing: true }));
    try {
      await axios.post(`${API}/subscriptions/reactivate`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setMessage({
        type: 'success',
        text: '🎉 Subscription reactivated! Your plan will continue as normal.'
      });
      fetchSubscription();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to reactivate subscription'
      });
    } finally {
      setReactivateModal({ isOpen: false, isProcessing: false });
    }
  };

  const openReactivateModal = () => {
    setReactivateModal({ isOpen: true, isProcessing: false });
  };

  const closeReactivateModal = () => {
    setReactivateModal({ isOpen: false, isProcessing: false });
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API}/subscriptions/cancel`,
        {
          cancel_immediately: cancelImmediately,
          reason: cancelReason
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );
      setShowCancelModal(false);
      fetchSubscription();
      setMessage({
        type: 'success',
        text: cancelImmediately 
          ? '✅ Your subscription has been cancelled.' 
          : '✅ Your subscription will be cancelled at the end of the billing period.'
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to cancel subscription'
      });
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        {/* Success/Error Message */}
        {message && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700'
              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700'
          }`}>
            {message.text}
          </div>
        )}
        
        <div className="text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Active Subscription
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Subscribe to a plan to get recurring credits and save money!
          </p>
          
          {/* Verify Button for users who completed checkout but webhook failed */}
          <button
            onClick={handleManualVerify}
            disabled={verifying}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline flex items-center justify-center gap-2 mx-auto"
          >
            {verifying ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Verifying payment...
              </>
            ) : (
              <>🔄 Just completed checkout? Click to verify</>
            )}
          </button>
        </div>
      </div>
    );
  }

  const creditsUsedPercent = subscription.credits_per_cycle > 0
    ? (subscription.credits_used_this_cycle / subscription.credits_per_cycle) * 100
    : 0;

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold mb-1">{subscription.plan_name}</h3>
              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[subscription.status] || STATUS_STYLES.active}`}>
                {subscription.status?.toUpperCase()}
              </span>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">${subscription.price_usd}</div>
              <div className="text-blue-100">{BILLING_CYCLE_LABELS[subscription.billing_cycle]}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Credits Section */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Credits This Cycle
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {subscription.credits_used_this_cycle} / {subscription.credits_per_cycle} used
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all"
                style={{ width: `${Math.min(creditsUsedPercent, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {subscription.credits_remaining} credits remaining
              </span>
              {subscription.rollover_credits > 0 && (
                <span className="text-sm text-green-600 dark:text-green-400">
                  +{subscription.rollover_credits} rolled over
                </span>
              )}
            </div>
          </div>

          {/* Billing Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">Current Period</div>
              <div className="font-medium text-gray-900 dark:text-white">
                {formatDate(subscription.current_period_start)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                to {formatDate(subscription.current_period_end)}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">Next Billing</div>
              <div className="font-medium text-gray-900 dark:text-white">
                {subscription.cancel_at_period_end 
                  ? 'Cancels at period end'
                  : formatDate(subscription.next_billing_date)
                }
              </div>
              {!subscription.cancel_at_period_end && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  +{subscription.credits_per_cycle} credits
                </div>
              )}
            </div>
          </div>

          {/* Warning if cancelling */}
          {subscription.cancel_at_period_end && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">⚠️</span>
                  <div>
                    <div className="font-medium text-yellow-800 dark:text-yellow-200">
                      Subscription Ending
                    </div>
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      Your subscription will end on {formatDate(subscription.current_period_end)}.
                      Your remaining credits will still be available until then.
                    </div>
                  </div>
                </div>
                <button
                  onClick={openReactivateModal}
                  className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                >
                  🔄 Reactivate
                </button>
              </div>
            </div>
          )}

          {/* Success/Error Message */}
          {message && (
            <div className={`p-3 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700'
                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700'
            }`}>
              {message.text}
            </div>
          )}

          {/* Actions */}
          {subscription.status === 'active' && !subscription.cancel_at_period_end && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Cancel Subscription?
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to cancel your {subscription.plan_name} subscription?
            </p>

            <div className="mb-4">
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="cancelType"
                  checked={!cancelImmediately}
                  onChange={() => setCancelImmediately(false)}
                  className="text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Cancel at period end</div>
                  <div className="text-sm text-gray-500">Keep access until {formatDate(subscription.current_period_end)}</div>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 mt-2">
                <input
                  type="radio"
                  name="cancelType"
                  checked={cancelImmediately}
                  onChange={() => setCancelImmediately(true)}
                  className="text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Cancel immediately</div>
                  <div className="text-sm text-gray-500">Lose access right away (no refund)</div>
                </div>
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason for cancelling (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Help us improve..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={reactivateModal.isOpen}
        onClose={closeReactivateModal}
        onConfirm={handleReactivate}
        title="Reactivate Subscription?"
        message={
          <div>
            <p className="mb-2">Reactivate your <strong>{subscription?.plan_name}</strong> subscription?</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your subscription will continue as normal and you&apos;ll be billed on the next billing date.
            </p>
          </div>
        }
        type="success"
        icon="🔄"
        confirmText="Reactivate"
        cancelText="Cancel"
        isProcessing={reactivateModal.isProcessing}
      />
    </>
  );
};

export default CurrentSubscription;
